import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineTag,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    categoryType: '',
    categoryName: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadCategories();
    } else {
      setError('Please login to view categories');
      setLoading(false);
    }
  }, [tenantId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getCategories(tenantId);
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories. Please try again.');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCategories = categories.filter((category) => {
    // Hide hazard categories as they have their own management page
    if (category.categoryType?.toLowerCase() === "hazard") return false;

    return (
      category.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.categoryType?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      categoryType: '',
      categoryName: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      categoryType: category.categoryType || '',
      categoryName: category.categoryName || '',
      status: category.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteCategory(id);
      await loadCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete category. Please try again.');
      console.error('Error deleting category:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.categoryName.trim()) {
      errors.categoryName = 'Category name is required';
    } else if (formData.categoryName.length > 100) {
      errors.categoryName = 'Category name must be less than 100 characters';
    }
    if (formData.categoryType && formData.categoryType.length > 50) {
      errors.categoryType = 'Category type must be less than 50 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const categoryData = {
        tenantId: tenantId,
        categoryType: formData.categoryType.trim() || null,
        categoryName: formData.categoryName.trim(),
        status: formData.status,
      };

      if (editingCategory) {
        categoryData.categoryId = editingCategory.categoryId;
        await masterDataService.updateCategory(editingCategory.categoryId, categoryData);
      } else {
        await masterDataService.createCategory(categoryData);
      }

      await loadCategories();
      setShowModal(false);
      setFormData({ categoryType: '', categoryName: '', status: 'Active' });
      setEditingCategory(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save category. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ categoryType: '', categoryName: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading categories..." />;
  }

  return (
    <div className="categories-page pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h2>Categories</h2>
          <p>Manage categories for classification</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Category
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="table-info">
            {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
          </div>
        </div>

        {filteredCategories.length === 0 ? (
          <div className="empty-state">
            <HiOutlineTag className="empty-icon" />
            <h3>No categories found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first category'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Category
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Category Type</th>
                <th>Category Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category, index) => (
                <tr key={category.categoryId}>
                  <td>{index + 1}</td>
                  <td>{category.categoryType || '-'}</td>
                  <td className="name-cell">{category.categoryName}</td>
                  <td>
                    <span className={`status-badge ${category.status?.toLowerCase()}`}>
                      {category.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {category.createdAt
                      ? new Date(category.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(category)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(category)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCategory ? 'Edit Category' : 'New Category'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="category-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingCategory
                  ? 'Updating...'
                  : 'Creating...'
                : editingCategory
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="category-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="categoryType">Category Type</label>
              <input
                type="text"
                id="categoryType"
                value={formData.categoryType}
                onChange={(e) =>
                  setFormData({ ...formData, categoryType: e.target.value })
                }
                className={formErrors.categoryType ? 'error' : ''}
                placeholder="e.g., Kaizen, RCA, OPL"
                maxLength={50}
              />
              {formErrors.categoryType && (
                <span className="field-error">{formErrors.categoryType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="categoryName">
                Category Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="categoryName"
                value={formData.categoryName}
                onChange={(e) =>
                  setFormData({ ...formData, categoryName: e.target.value })
                }
                className={formErrors.categoryName ? 'error' : ''}
                placeholder="Enter category name"
                maxLength={100}
              />
              {formErrors.categoryName && (
                <span className="field-error">{formErrors.categoryName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.categoryId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.categoryName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The category will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Categories;

